import type { Metadata } from 'next';

import { getTranslation, locales, type Locale } from './i18n';

export const BASE_URL = 'https://www.open.cx';

/**
 * Stable `@id` for Open's canonical Organization node, which RootDocument
 * emits once per page. Every other schema node that needs to name Open —
 * `author`, `publisher`, `provider` — should reference this rather than
 * inlining a duplicate Organization object, so search engines resolve one
 * entity instead of reconciling many near-identical ones.
 */
export const ORGANIZATION_ID = `${BASE_URL}/#organization`;

/**
 * The site's fallback social preview image — the one static card in `public/`.
 *
 * Prefer `ogImage()` below, which renders the page's own title. This constant
 * is for the handful of places that have no title to render.
 *
 * Every other static OG file this codebase referenced was a 404:
 * `og-image.png`, `og-templates.png`, `og-integrations.png` and ~35
 * `og-blog-*.png` were named in metadata but never committed to `public/`.
 * Of the 39 distinct `og:image` URLs the site advertised across its 909
 * sitemap entries, this was the only one that returned an image.
 */
export const OG_IMAGE_URL = `${BASE_URL}/og-image.jpg`;

/**
 * A route's path in one locale, using the site's own convention: English is
 * unprefixed, everything else carries its locale. Same rule as
 * `buildAlternates`, pulled out so a caller that only needs the path does not
 * have to build a whole alternates map and cast `.canonical` back to a string.
 */
export function localePath(locale: Locale, route: string): string {
  const clean = route === '/' ? '' : route;
  return locale === 'en' ? clean || '/' : `/${locale}${clean}`;
}

/**
 * The social preview image for a page: a screenshot of that page.
 *
 * Returns a URL into `/api/og`, which loads `route` in a headless browser and
 * returns what it saw. A shared link therefore previews as the page the reader
 * is about to land on, rather than as a house image or a drawn card.
 *
 * `route` is the page's own path, locale prefix included — `/pricing`,
 * `/ar/pricing`. Pass the canonical path, not the request path, so the /en
 * alias and the un-prefixed default resolve to the same screenshot and the
 * same cache entry.
 *
 * It has to be `/api/og` rather than Next's `opengraph-image.tsx` convention:
 * the middleware rewrites every extensionless path to `/${defaultLocale}${p}`,
 * so a file-convention OG route resolves to `/en/.../opengraph-image`, which
 * `[locale]` does not define. `/api` is the one prefix the middleware skips.
 * See the comment at the top of `app/api/og/route.ts`.
 */
export function ogImage(route: string): string {
  const path = route.startsWith('http') ? new URL(route).pathname : route;
  return `${BASE_URL}/api/og?p=${encodeURIComponent(path || '/')}`;
}

type OGImageEntry = {
  url: string;
  secureUrl: string;
  type: string;
  width: number;
  height: number;
  alt: string;
};

/**
 * `ogImage()` shaped as the object `Metadata['openGraph']['images']` wants, so
 * a call site can pass one value instead of restating the dimensions at each
 * of ~130 of them.
 *
 * Carries every field the platforms actually read, not just the ones Open
 * Graph requires. Slack, Telegram and Discord are happy with `og:image`
 * alone; Facebook and LinkedIn also look for `og:image:secure_url`; WhatsApp
 * wants `og:image:type`; and X reads `twitter:image` with its own alt. Each
 * one that is missing degrades silently on that platform and nowhere else,
 * which is why they are set in one place rather than per page.
 */
export function ogImageEntry(route: string, alt: string): OGImageEntry {
  const url = ogImage(route);
  return {
    url,
    // Facebook and LinkedIn both still read `og:image:secure_url`, and
    // WhatsApp will skip an image whose type it cannot determine up front.
    // Everything here is https and png, so both are free to state.
    secureUrl: url,
    type: 'image/png',
    width: 1200,
    height: 630,
    alt,
  };
}

/**
 * The same image shaped for `twitter.images`.
 *
 * Separate from `ogImageEntry` only because X reads `twitter:image:alt` and
 * ignores the og: alt, and because passing a bare string — which most call
 * sites used to do — emits no alt at all.
 */
export function twitterImageEntry(
  route: string,
  alt: string,
): { url: string; alt: string } {
  return { url: ogImage(route), alt };
}

/**
 * The account attribution X uses to credit the post.
 *
 * Spread into a page's `twitter` block. A page that declares its own `twitter`
 * REPLACES the one it inherits rather than merging with it, so every page that
 * set a title and description of its own was silently dropping `twitter:site`
 * and `twitter:creator`.
 */
export const TWITTER_ACCOUNT = {
  site: '@open_cx',
  creator: '@open_cx',
} as const;

/**
 * Append the brand to a page title, deterministically.
 *
 * The `[locale]` layout defines `title.template: '%s | Open'`, but a template
 * only reaches a child whose nearest title-setting ancestor is the one that
 * declared it. `industries/layout.tsx` — and every other intermediate layout
 * that sets a plain string title — consumes it, so its own children fall out
 * of scope and render with no brand at all. /channels/voice,
 * /helpdesks/zendesk, /industries/healthcare and /tools/roi-calculator were
 * all shipping title tags with no "Open" in them.
 *
 * Two routes had been patched around this by hardcoding "| Open" into their
 * title, which then doubled on the pages that *did* still get the template:
 * /whitelabel rendered "... | Open | Open".
 *
 * Emitting `title.absolute` sidesteps the inheritance question entirely — an
 * absolute title ignores any ancestor template, so output is identical at
 * every depth.
 */
function withBrand(title: string): string {
  return title.endsWith('| Open') ? title : `${title} | Open`;
}

/**
 * Build a canonical URL + hreflang `languages` map for a route.
 *
 * - English URLs are unprefixed (`/foo`), Arabic URLs are prefixed (`/ar/foo`).
 *   This matches the middleware in `src/middleware.ts`.
 * - `availableLocales` lets a page restrict hreflang to the locales that
 *   genuinely exist. Routes that have no Arabic version (legal pages, sales
 *   decks, untranslated tools) should pass `['en']` so we don't declare
 *   `/ar/...` URLs that don't resolve. Defaults to all locales.
 *
 * Use this everywhere that sets `alternates` in `Metadata`. Replacing partial
 * `alternates: { canonical: '...' }` overrides with this helper is what
 * eliminates the bulk of Semrush hreflang errors.
 */
export function buildAlternates(
  locale: Locale,
  route: string,
  availableLocales: Locale[] = locales,
): NonNullable<Metadata['alternates']> {
  const localeUrl = (loc: Locale): string =>
    loc === 'en' ? `${BASE_URL}${route}` : `${BASE_URL}/${loc}${route}`;

  // If the current locale isn't in `availableLocales`, the page is being
  // served from a URL that doesn't have its own translation. Canonicalise
  // it to the first available locale (typically `en`) so Google
  // deduplicates against the real source instead of treating the
  // un-translated /ar/... URL as a separate page.
  const canonicalLocale = availableLocales.includes(locale)
    ? locale
    : (availableLocales[0] ?? 'en');
  const canonical = localeUrl(canonicalLocale);

  const languages: Record<string, string> = {};
  for (const loc of availableLocales) {
    languages[loc] = localeUrl(loc);
  }
  languages['x-default'] = localeUrl(availableLocales[0] ?? 'en');

  return { canonical, languages };
}

// Route to metadata key mapping
const routeMetadata: Record<
  string,
  { title: string; description?: string; keywords?: string[] }
> = {
  '/about-open': {
    title: 'About Open - AI That Actually Works',
    description:
      'We make AI that actually works. Meet the team behind the AI customer support platform trusted by TicketSwap, Mollie, and MoneyGram.',
    keywords: [
      'about open',
      'AI company',
      'customer support startup',
      'AI team',
    ],
  },
  '/agent5': {
    title: 'Agent 5 — The AI Engine for Customer Support',
    description:
      'The AI engine powering all your customer communications. 77% automation rate. Works with Zendesk, Intercom, Salesforce, and more.',
    keywords: [
      'AI agent',
      'customer support AI',
      'AI automation',
      'support automation',
      'Agent 5',
      'AI engine',
    ],
  },
  '/channels': {
    title: 'Channels - AI Communication Platform',
    description:
      'AI-powered communication across all channels - voice, chat, email, WhatsApp, social, and Slack. One AI, every channel.',
    keywords: [
      'omnichannel AI',
      'AI communication',
      'voice AI',
      'chat AI',
      'email AI',
      'WhatsApp AI',
    ],
  },
  '/channels/voice': {
    title: 'AI Phone Support That Sounds Human',
    description:
      'The agent answers on the first ring in the language the caller used, books and reschedules, resolves account questions, and hands to a person with full context.',
  },
  '/channels/chat': {
    title: 'AI Web Chat for Customer Support',
    description:
      'One embed on your site and the agent answers instantly — order status, account questions, policy answers — escalating to your team in the same thread when it should.',
  },
  '/channels/email': {
    title: 'AI Email Support on Your Own Domain',
    description:
      'The agent reads, drafts and sends from your own support address, keeps the thread intact, and leaves anything it should not answer for a human to pick up.',
  },
  '/channels/whatsapp': {
    title: 'AI WhatsApp Support with Voice Notes and Images',
    description:
      'Customers send a voice note or a photo of the problem and get a real answer back on WhatsApp Business — same agent, same knowledge, no separate bot to maintain.',
  },
  '/channels/social': {
    title: 'AI Support for Instagram and Messenger',
    description:
      'DMs and comment replies handled by the same agent that runs your other channels, so a question asked on Instagram gets the answer your help centre would give.',
  },
  '/channels/slack': {
    title: 'Internal AI Support Inside Slack',
    description:
      'Your team asks in the channel they already use and gets the answer from your own documentation — instead of interrupting the two people who happen to know it.',
  },
  '/companion': {
    title: 'Companion - A Senior Support Ops Expert In A Chat',
    description:
      'The AI assistant inside Open: diagnose performance drops, rewrite AI instructions with inline diffs, debug any ticket, run live test sessions. 140+ tools.',
    keywords: [
      'Open Companion',
      'AI support ops assistant',
      'AI instructions editor',
      'AI session debugger',
      'support operations AI',
      'AI workspace assistant',
    ],
  },
  '/customer-insights': {
    title: 'Customer Insights — What Customers Contact You About',
    description:
      'Every conversation grouped by what the customer actually wanted, so contact reasons come from your tickets rather than from a tagging scheme somebody guessed at.',
  },
  '/customer-stories': {
    title: 'Customer Stories',
    description:
      'How leading companies use Open to transform customer support.',
  },
  '/customer-stories/cookinglife': {
    title: 'Cookinglife - 70% Support Automation with Open',
    description:
      'How Cookinglife serves 1M+ customers and automates 70% of support with Open.',
  },
  '/customer-stories/deonlinedrogist': {
    title: 'De Online Drogist - 73% Automation with Open',
    description:
      'How De Online Drogist automates 73% of support and runs 25+ AI actions with Open.',
  },
  '/customer-stories/mollie': {
    title: 'Mollie - Scaling Support for 250K+ Businesses with Open',
    description:
      'How Mollie supports 250,000+ businesses across Europe and automates 60%+ of support with Open.',
  },
  '/customer-stories/moneygram': {
    title: 'MoneyGram - 70% Automation Across 200+ Countries',
    description:
      'How MoneyGram supports 55M customers across 200+ countries and automates 70% of support with Open.',
  },
  '/customer-stories/more': {
    title: 'More.com - 67% Automation Across 30M Tickets',
    description:
      'How More.com sold 30M tickets across Europe and automated 67% of support with Open.',
  },
  '/customer-stories/ocn': {
    title: 'OCN - Automating Car Subscription Support in Mexico',
    description:
      'How OCN automates 60% of support for 7.5K vehicles and Mexico car subscriptions with Open.',
  },
  '/customer-stories/oto': {
    title: 'OTO - 77% Automation with 90%+ CSAT',
    description:
      'How OTO automates more than 77% of last-mile delivery support across MENA with 90%+ CSAT using Open.',
  },
  '/customer-stories/qoyod': {
    title: 'Qoyod - 77% Automation for 25,000+ Businesses',
    description:
      'How Qoyod automates 77% of support for 25,000+ businesses with Open.',
  },
  '/customer-stories/sneleentaxi': {
    title: 'Snel Een Taxi - AI Support for On-Demand Mobility',
    description:
      'How Snel Een Taxi automates customer support for its on-demand taxi service with Open.',
  },
  '/customer-stories/ticketswap': {
    title: 'TicketSwap - 67% Automation Across 19M Users',
    description:
      'How TicketSwap automated 67% of 19 million user support operations with Open.',
  },
  '/customer-stories/trunkrs': {
    title: 'Trunkrs - 80% Automation with 4.8/5 Customer Rating',
    description:
      'How Trunkrs automates 80% of delivery support with a 4.8/5 customer rating using Open.',
  },
  '/demo': {
    title: 'Book a Demo — See Agent 5 on Your Own Tickets',
    description:
      'A working session, not a slide deck. We connect your helpdesk, run the agent against your real tickets, and show you what it would and would not have resolved.',
  },
  '/helpdesks': {
    title: 'Helpdesks - AI Helpdesk Integration',
    description:
      'Integrate AI with your existing helpdesk platform. Works with Zendesk, Intercom, Salesforce, HubSpot, Freshdesk, and Twilio Flex.',
    keywords: [
      'AI helpdesk',
      'Zendesk AI',
      'Intercom AI',
      'Salesforce AI',
      'helpdesk integration',
      'customer support AI',
    ],
  },
  '/industries': {
    title: 'Industries',
    description: 'AI customer support solutions for every industry.',
  },
  '/industries/financial-services': {
    title: 'AI Customer Support for Financial Services',
    description:
      'Card, payment and account questions resolved inside your existing controls — with anything touching fraud, sanctions or a regulated decision handed to a human.',
  },
  '/industries/healthcare': {
    title: 'AI Customer Support for Healthcare',
    description:
      'Appointment changes, coverage questions and intake handled automatically, with clinical questions routed to a person and every escalation logged.',
  },
  '/industries/mobility': {
    title: 'AI Customer Support for Mobility and Transport',
    description:
      'Riders, drivers and fleet questions handled in one place — bookings, cancellations, fares and damage claims — in the language the customer wrote in.',
  },
  '/industries/technology': {
    title: 'AI Customer Support for SaaS and Technology',
    description:
      'Onboarding questions, API and billing tickets answered from your own docs, with reproducible bugs escalated to engineering carrying the full thread.',
  },
  '/industries/travel-hospitality': {
    title: 'AI Customer Support for Travel and Hospitality',
    description:
      "Bookings, changes, cancellations and disruption handled around the clock in the guest's language, with the cases that need judgement passed to your team.",
  },
  '/industries/tour-operators': {
    title: 'AI Customer Support for Tour & Activity Operators',
    description:
      'Agent 5 is the 24/7 tour desk: bookings, reschedules, cancellations and guest questions in 100+ languages. Trusted by RealNZ and Pacific Whale Foundation.',
    keywords: [
      'tour operator customer support',
      'AI for tour operators',
      'activity booking support',
      'tours and activities AI',
      'experience operator support',
      'booking questions automation',
    ],
  },
  '/whitelabel': {
    title: 'White-Label AI Customer Support for Partners',
    description:
      'Launch your own AI customer support product under your brand. FareHarbor, part of the Booking.com group, white-labels Open for thousands of its merchants.',
    keywords: [
      'white label AI support',
      'white label customer support',
      'partner platform',
      'reseller AI support',
      'embed AI support',
      'AI support API',
      'launch AI product line',
    ],
  },
  '/knowledge-gap-detection': {
    title: 'Knowledge Gap Detection for Support Teams',
    description:
      'When the agent cannot answer, the question is logged as a gap, in the words the customer used — so you write the article that was missing, not the one you assumed.',
  },
  '/app-builder': {
    title: 'App Builder - Self-Building Software',
    description:
      'Ask Companion for a feature that never existed and it builds a working app into Open. Customers have built 1,000+ features and 2,000+ apps in two months.',
    keywords: [
      'AI app builder',
      'self-building software',
      'build features by prompting',
      'no-code CX platform',
      'extend customer support platform',
      'prompt to app',
      'custom support apps',
    ],
  },
  '/open-telephony': {
    title: 'Open Telephony - The AI Phone System',
    description:
      'An AI-native business phone system: numbers in 100+ countries, IVR, routing, transcription, SMS, and a 24/7 AI receptionist. Works with 30+ providers.',
    keywords: [
      'AI phone system',
      'business phone system',
      'AI receptionist',
      'cloud telephony',
      'IVR',
      'call routing',
      'voice AI',
      'Aircall alternative',
      'AI voice agent',
    ],
  },
  '/outbound-sequencing': {
    title: 'AI Outbound Sequences for Proactive Support',
    description:
      'Reach customers before they contact you — delivery delays, renewals, failed payments, appointment reminders — across voice, WhatsApp and email, with replies handled.',
  },
  '/sub-processes': {
    title: 'Sub-processes — Reusable Steps Inside a Workflow',
    description:
      'Write a procedure once and call it from any workflow. Verification, refund eligibility, address changes — defined in one place, so fixing it once fixes it everywhere',
  },
  '/templates': {
    title: 'AI Workflow Templates for Support Teams',
    description:
      'Ready-made automations for the tickets every support team gets — order tracking, refunds, password resets, appointment changes — editable before you switch them on.',
  },
  '/tools': {
    title: 'Tools - AI Support Tools',
    description: 'AI-powered customer support tools.',
  },
  '/workflows': {
    title: 'AI Workflow Automation for Customer Support',
    description:
      'Build the steps your team repeats into workflows the agent runs itself — lookups, updates, refunds, escalations — with a human in the loop wherever you want one.',
  },
  '/helpdesks/zendesk': {
    title: 'Use Open in Zendesk - No Migration Needed',
    description:
      'Use Open right in your Zendesk helpdesk. No migration needed, effortless integration in under an hour, all features from day one.',
    keywords: [
      'Zendesk AI',
      'Zendesk automation',
      'Zendesk integration',
      'AI for Zendesk',
      'Zendesk chatbot',
    ],
  },
  '/helpdesks/hubspot': {
    title: 'AI Agent for HubSpot Service Hub',
    description:
      'AI agent for HubSpot Service Hub. Automate support tickets with enterprise-grade AI. No migration, works in your existing HubSpot.',
    keywords: [
      'HubSpot AI',
      'HubSpot Service Hub AI',
      'HubSpot automation',
      'AI for HubSpot',
    ],
  },
  '/helpdesks/intercom': {
    title: 'Use Open in Intercom - No Migration Needed',
    description:
      'Use Open right in your Intercom helpdesk. No migration needed, effortless integration in under an hour, all features from day one. Better than Fin AI.',
    keywords: [
      'Intercom AI',
      'Intercom automation',
      'Fin alternative',
      'AI for Intercom',
      'Intercom chatbot',
    ],
  },
  '/helpdesks/salesforce': {
    title: 'AI Agent for Salesforce Service Cloud',
    description:
      'Enterprise case automation without a consulting engagement. Open runs inside Service Cloud, keeps it as the system of record, and integrates in under an hour.',
    keywords: [
      'Salesforce AI',
      'Service Cloud AI',
      'Salesforce automation',
      'AI for Salesforce',
    ],
  },
  '/helpdesks/freshdesk': {
    title: 'Use Open in Freshdesk - No Migration Needed',
    description:
      'Use Open right in your Freshdesk helpdesk. No migration needed, effortless integration in under an hour, all features from day one.',
    keywords: [
      'Freshdesk AI',
      'Freshdesk automation',
      'Freddy alternative',
      'AI for Freshdesk',
    ],
  },
  '/helpdesks/twilio-flex': {
    title: 'Twilio Flex AI Agent',
    description:
      'AI agent for Twilio Flex contact center. The AI capabilities Twilio doesnt have. Works with your existing Flex setup.',
    keywords: [
      'Twilio Flex AI',
      'Twilio automation',
      'contact center AI',
      'AI for Twilio',
    ],
  },
  '/helpdesks/open': {
    title: 'Open Helpdesk',
    description:
      'The complete AI-first helpdesk platform. All channels, all AI features, built from the ground up for automation.',
    keywords: [
      'AI helpdesk',
      'AI-first helpdesk',
      'customer support platform',
      'omnichannel helpdesk',
    ],
  },
  '/cheatsheet': {
    title: 'AI Customer Support Cheatsheet',
    description:
      'The ultimate cheatsheet for AI-powered customer support. Best practices, metrics, and implementation guides.',
    keywords: ['AI cheatsheet', 'customer support guide', 'AI best practices'],
  },
  '/privacy-policy': {
    title: 'Privacy Policy',
    description:
      'Privacy policy for Open. Learn how we collect, use, and protect your data.',
  },
  '/cookie-policy': {
    title: 'Cookie Policy',
    description:
      'The cookies and trackers Open uses, what they do, and how to control them.',
  },
  '/terms': {
    title: 'Terms of Service',
    description:
      'Terms of service for using Open AI customer support platform.',
  },
  '/gdpr': {
    title: 'GDPR Compliance',
    description:
      'GDPR compliance information for Open. Enterprise-grade data protection and privacy.',
    keywords: [
      'GDPR',
      'data protection',
      'privacy compliance',
      'enterprise security',
    ],
  },
  '/legal': {
    title: 'Legal & Compliance',
    description:
      'Open legal and compliance center. Review our GDPR compliance, subprocessors, privacy policy, and terms of service.',
    keywords: [
      'legal',
      'compliance',
      'privacy policy',
      'terms of service',
      'GDPR',
      'subprocessors',
    ],
  },
  '/legal/standard-terms': {
    title: 'Standard Terms',
    description:
      'The Open Cloud Service Standard Terms that apply to every Open Cloud Service Agreement, with version history.',
    keywords: ['standard terms', 'cloud service agreement', 'terms', 'legal'],
  },
  '/legal/dpa-standard-terms': {
    title: 'DPA Standard Terms',
    description:
      'The Open Data Processing Agreement Standard Terms that apply to every Open DPA, with version history.',
    keywords: ['DPA', 'data processing agreement', 'GDPR', 'standard terms', 'legal'],
  },
  '/legal/sla': {
    title: 'SLA Standard Terms',
    description:
      'The Open Service Level Agreement Standard Terms: uptime, service credits and remedies, with version history.',
    keywords: ['SLA', 'service level agreement', 'uptime', 'legal'],
  },
  '/legal/dpa-annexes': {
    title: 'DPA Annexes',
    description:
      'Annex I (processing details) and Annex II (security measures) of the Open Data Processing Agreement, with version history.',
    keywords: ['DPA', 'annex', 'security measures', 'GDPR', 'legal'],
  },
  '/legal/price-list': {
    title: 'Price List',
    description:
      'Every Open product and how each one is charged, with upcoming changes and change history.',
    keywords: ['price list', 'billing', 'products', 'legal'],
  },
  '/incentive-terms': {
    title: 'Incentive Terms',
    description: 'Terms and conditions for Open incentive programs.',
  },
  '/integrations': {
    title: 'Integrations - Connect Your Data Sources',
    description:
      'Connect Open to your existing tools. 27+ integrations including Notion, Confluence, Google Docs, Slack, and more.',
    keywords: [
      'AI integrations',
      'knowledge base integration',
      'Notion AI',
      'Confluence AI',
      'data source integration',
    ],
  },
  '/blog': {
    title: 'Blog - AI Customer Support Insights',
    description:
      'Insights, guides, and comparisons for AI-powered customer support. Learn from industry experts.',
    keywords: [
      'AI blog',
      'customer support blog',
      'AI insights',
      'support automation',
    ],
  },
  '/open-academy': {
    title: 'Open Academy - AI Support Training',
    description:
      'Free courses on AI customer support. Learn to build, deploy, and optimize AI agents.',
    keywords: [
      'AI training',
      'customer support courses',
      'AI certification',
      'support academy',
    ],
  },
  '/news/announcing-our-new-funding-round': {
    title: 'Announcing Our New Funding Round',
    description:
      'Open announces new funding to accelerate AI customer support innovation.',
  },
  '/tools/ai-adoption-guide': {
    title: 'AI Adoption Guide',
    description:
      'Step-by-step guide to adopting AI in your customer support operations.',
  },
  '/tools/ai-readiness': {
    title: 'AI Readiness Assessment',
    description: 'Assess your organization readiness for AI customer support.',
  },
  '/tools/benchmark': {
    title: 'Support Benchmark Tool',
    description: 'Benchmark your support metrics against industry standards.',
  },
  '/tools/roi-calculator': {
    title: 'AI ROI Calculator',
    description: 'Calculate the ROI of implementing AI customer support.',
  },
  '/tools/support-budget': {
    title: 'Support Budget Calculator',
    description: 'Plan and optimize your customer support budget.',
  },
  '/tools/team-size-calculator': {
    title: 'Team Size Calculator',
    description: 'Calculate optimal support team size with AI automation.',
  },
};

/**
 * Resolve a route's title and description exactly as `generatePageMetadata`
 * does, so structured data can quote the same strings the page's own meta
 * uses. Returns null for a route with no entry, which keeps a caller from
 * inventing a name for a page it knows nothing about.
 */
export function getRouteMeta(
  route: string,
  locale: Locale,
): { title: string; description: string } | null {
  const metadata = routeMetadata[route];
  if (!metadata) return null;
  const t = getTranslation(locale);
  const titleKey = metadata.title as keyof typeof t.meta;
  const title = t.meta[titleKey] || metadata.title;
  const description =
    (t.meta as Record<string, string>)[metadata.description ?? ''] ||
    metadata.description ||
    '';
  return { title, description };
}

/**
 * Generate metadata for a given route and locale
 * @param route - The route path (e.g., '/channels', '/about-open')
 * @param locale - The locale (e.g., 'en', 'ar')
 * @returns Metadata object with localized title and description
 */
export function generatePageMetadata(
  route: string,
  locale: Locale,
  availableLocales: Locale[] = locales,
): Metadata {
  const t = getTranslation(locale);
  const metadata = routeMetadata[route];

  const alternates = buildAlternates(locale, route, availableLocales);
  const canonicalUrl = alternates.canonical as string;

  if (!metadata) {
    // Default metadata if route not found
    const defaultTitle =
      t.meta['Open - The unified AI customer support platform'];
    const defaultDescription =
      t.meta['Radically upgrade customer support and communication with AI.'];

    return {
      title: { absolute: withBrand(defaultTitle) },
      description: defaultDescription,
      robots: {
        index: true,
        follow: true,
        googleBot: {
          index: true,
          follow: true,
          'max-video-preview': -1,
          'max-image-preview': 'large',
          'max-snippet': -1,
        },
      },
      openGraph: {
        title: defaultTitle,
        description: defaultDescription,
        type: 'website',
        url: canonicalUrl,
        siteName: 'Open',
        locale: locale === 'ar' ? 'ar_SA' : 'en_US',
        images: [ogImageEntry(canonicalUrl, defaultTitle)],
      },
      twitter: {
        card: 'summary_large_image',
        title: defaultTitle,
        description: defaultDescription,
        site: '@open_cx',
        creator: '@open_cx',
        images: [twitterImageEntry(canonicalUrl, defaultTitle)],
      },
      alternates,
    };
  }

  // Type-safe access to meta translations
  const titleKey = metadata.title as keyof typeof t.meta;
  const title = t.meta[titleKey] || metadata.title;
  const description =
    (t.meta as Record<string, string>)[metadata.description ?? ''] ||
    metadata.description ||
    '';

  return {
    title: { absolute: withBrand(title) },
    description,
    keywords: metadata.keywords,
    robots: {
      index: true,
      follow: true,
      googleBot: {
        index: true,
        follow: true,
        'max-video-preview': -1,
        'max-image-preview': 'large',
        'max-snippet': -1,
      },
    },
    openGraph: {
      title,
      description,
      type: 'website',
      url: canonicalUrl,
      siteName: 'Open',
      locale: locale === 'ar' ? 'ar_SA' : 'en_US',
      images: [ogImageEntry(canonicalUrl, title)],
    },
    twitter: {
      card: 'summary_large_image',
      title,
      description,
      site: '@open_cx',
      creator: '@open_cx',
      images: [twitterImageEntry(canonicalUrl, title)],
    },
    alternates,
  };
}
